import { AnyObject } from '@triare/auth-redux';
import { DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate } from '../fetch';
import { SubCategoryShort } from './subCategories';
import { FileData } from './files';
import { PaginationProps } from '../../store/types';
import { Seo } from '../../types/seo';

export type CategoryList = PaginationProps;

export interface CategoryShort {
  id: string;
  isActive: boolean;
  name: string;
  header: string;
  description: string;
  shortDescription: string;
  author: string;
  file: FileData;
  image: FileData;
  seo: Seo;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoriesGetParams extends PagingParams {
  orderByColumn?: 'name';
}

export const useCategoriesGet = (): FetchGet<
  PagingDataResponse<CategoryShort>,
  CategoriesGetParams
> => useFetchGet(
  'admin/categories',
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export const useCategoriesForSelectGet = (): FetchGet<
  Pick<CategoryShort, 'id' | 'name'>[],
  CategoriesGetParams
> => useFetchGet(
  'admin/categories/for-select',
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export interface CategoryAndSubCategory extends CategoryShort {
  subCategories: SubCategoryShort[];
}

export const useCategoriesAndSubCategoryGet = (): FetchGet<
  PagingDataResponse<CategoryAndSubCategory>,
  CategoriesGetParams
> => useFetchGet(
  'admin/categories/all',
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export interface CategoryById extends CategoryShort {
  comments: AnyObject;
  subCategories: SubCategoryShort[];
}

export const useCategoryGetById = (categoryId?: string): FetchGet<
  CategoryById
> => useFetchGetId(
  'admin/categories',
  categoryId,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export interface CategoryCreateResponse {
  id: string;
  isActive: boolean;
  name: string;
  header: string;
  description: string;
  fileId: string;
  author: string;
}

export interface CategoryCreateData {
  id: string;
  name?: string;
  isActive?: boolean;
  header?: string;
  description?: string;
  shortDescription?: string;
  author?: string;
  fileId?: string;
}

export const useCategoryCreate = (): FetchCreate<
  CategoryCreateResponse,
  DefaultFetchError,
  CategoryCreateData
> => useFetchCreate(
  'admin/categories',
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export type CategoryUpdateResponse = CategoryCreateResponse;

export type CategoryUpdateData = Partial<CategoryCreateData>;

export const useCategoryUpdate = (categoryId?: string): FetchUpdate<
  CategoryUpdateResponse,
  DefaultFetchError,
  CategoryUpdateData
> => useFetchUpdate(
  'admin/categories',
  categoryId,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export const useCategoryDelete = (categoryId?: string): FetchDelete => useFetchDelete(
  'admin/categories',
  categoryId,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export default {
  useCategoriesGet,
  useCategoriesAndSubCategoryGet,
  useCategoryGetById,
  useCategoryCreate,
  useCategoryUpdate,
};
